import { BigcommerceHandler } from './apiHandler';
import {
  AlertStates,
  FulfillmentRestriction,
  InventoryStatus,
  ProductTagFromHit,
  ShippingRestrictions,
  VariantInventory,
} from './product';

export interface OptionSelections {
  option_id?: number;
  option_value?: string | number | Date;
  name?: string;
  value?: string | number | Date;
}

interface LineItemOptions {
  name: string;
  nameId: number;
  value: string;
  valueId?: number;
}

export interface ItemBody {
  productName?: string;
  customerId?: number;
  productId: number;
  variantId: number;
  quantity: number;
  optionSelections?: OptionSelections[];
}

export interface ItemBodyRequiredQuantity extends ItemBody {
  quantity: number;
}

export interface CartMessage {
  key: string;
  type: keyof typeof AlertStates;
  productName?: string;
  productLink?: string;
  attemptedToAdd?: number;
  added?: number;
  addedItem?: {
    productId: number;
    variantId: number;
  };
}

export interface LineItem {
  id?: string;
  options?: LineItemOptions[];
  variant_id: number;
  product_id: number;
  quantity: number;
}

export enum InlineCartMessageType {
  addToSaveForLater = 'addToSaveForLater',
}

interface InlineCartMessage {
  removedMessage: string;
}

export interface CartLineItem extends LineItem {
  id: string;
  code: string;
  sku: string;
  name: string;
  image_url: string;
  url: string;
  list_price: number;
  extended_list_price: number;
  sale_price: number;
  discounts: Promotion[];
  extended_sale_price: number;
  errorMessages?: CartMessage[];
  shipping_restrictions: ShippingRestrictions;
  /** Variant-level ship/pickup restriction; null when unrestricted or the flag is off. */
  fulfillment_restriction?: FulfillmentRestriction | null;
  priceType?: string;
  coupons: Coupon[];
  coupon_amount?: number;
  discount_amount?: number;
  removedMessage?: string;
  tags?: ProductTagFromHit[];
  noItemDescription?: boolean;
  original_price?: number;
  brand?: string | null;
  category?: string | null;
  inventoryAggregated?: VariantInventory | null;
  variantBadge?: string | null;
  isMarketplace: boolean;
  marketplaceSellerName: string;
  storeInventory: Record<string, InventoryStatus>;
  isBike: boolean;
  minPurchaseQuantity: number | null;
  hasMemberPriceDifference?: boolean;
  hasCustomerGroupPriceDifference?: boolean;
  customerExtendedListPrice?: number;
}

export interface CartLineItems {
  custom_items: any[];
  digital_items: CartLineItem[];
  gift_certificates: any[];
  physical_items: CartLineItem[];
}

export type AddItemBody = { itemId: string; item: ItemBody };

export type AddItemsBody = { items: ItemBody[] };

export type UpdateItemBody = { itemId: string; item: ItemBody };

export type RemoveItemBody = { itemId: string };

export type CartHandlers = {
  getCart: BigcommerceHandler<Cart, { cartId?: string }>;
  addItem: BigcommerceHandler<Cart, { cartId?: string } & Partial<AddItemBody>>;
  addItems: BigcommerceHandler<
    Cart,
    { cartId?: string } & Partial<AddItemsBody>
  >;
  updateItem: BigcommerceHandler<
    Cart,
    { cartId?: string } & Partial<UpdateItemBody>
  >;
  removeItem: BigcommerceHandler<
    Cart,
    { cartId?: string } & Partial<RemoveItemBody>
  >;
  updateCustomerId: BigcommerceHandler<
    Cart,
    { cartId?: string; customerId?: number }
  >;
};

interface PromotionNotification {
  type: string;
  content: string;
}

export interface Promotion {
  id: number;
  name: string;
  rules: [
    {
      action: {
        shipping?: {
          free_shipping: boolean;
          zone_ids: number[];
        };
      };
      condition?: {
        cart?: {
          minimum_spend?: string;
        };
      };
    }
  ];
  notifications: PromotionNotification[];
  discounted_amount: number;
}

// TODO: this type should match:
// https://developer.bigcommerce.com/api-reference/cart-checkout/server-server-cart-api/cart/getacart#responses
export type Cart = {
  cartData?: CartData;
  cartMessages?: CartMessage[];
} | null;
export interface CartData {
  id?: string;
  parent_id?: string;
  customer_id: number;
  email: string;
  currency: { code: string };
  tax_included: boolean;
  base_amount: number;
  discount_amount: number;
  cart_amount: number;
  line_items: CartLineItems;
  channel_id: number;
  redirect_urls: {
    checkout_url: string;
  };
  updated_time?: string;
  coupons: Coupon[];
  estimated_total: number;
  isBike: boolean;
  // TODO: add missing fields
}

interface PromotionNotification {
  type: string;
  content: string;
}

interface Coupon {
  id: number;
  code: string;
  name: string;
  coupon_type: string;
  discounted_amount: number;
}

export interface CartItemsBySeller {
  sellerName: string;
  items: CartLineItem[];
}

export enum CartShipToHomeType {
  REGULAR = 'regular',
  BIKE_ONLY = 'bike_only',
  MULTICARRIER = 'multicarrier',
  OVER_SIZE_SURCHARGE = 'over_size_surcharge',
  EXPRESS = 'express',
  GROUND_ONLY = 'ground_only',
}
